import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react';

import { portalApi } from '../../../../api';
import BackButton from '../../../../components/BackButton';
import StackedButtonGroup from '../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import RequirementTemplateDropdownMenu from '../../../../components/RequirementTemplateDropdownMenu';
import SaveChangesModal from '../../../../components/SaveChangesModal';
import { CypressIds } from '../../../../constants/cypressIds';
import { createSuccessToast, createToast } from '../../../../redux/modules/app';
import { updateInsuranceRequirements } from '../../../../redux/modules/insured';
import { hideModal, showModal } from '../../../../redux/modules/modal';
import { insuredRequirementTemplateBuilder } from '../../../../redux/reducer';
import keys from '../../../../services/models/company/keys';
import insuredKeys from '../../../../services/models/insured/keys';
import recordKeys from '../../../../services/models/record/keys';
import { queryClient } from '../../../../utils/queryClientUtil';
import useUploadContractFlow from '../../../TemplatesListPage/components/modals/useUploadContractFlow';

const UpdateRequirementsHeader = () => {
  const dispatch = useDispatch();
  const { companyId, insuredId, requirementId } = useParams();
  const companyIdNumber = Number(companyId);
  const currentTemplate = useSelector(insuredRequirementTemplateBuilder.selectCurrentRequirementTemplate);
  const updatingRequirements = useSelector(insuredRequirementTemplateBuilder.selectUpdatingRequirements);
  const { url } = useRouteMatch();
  const history = useHistory();
  const insuredUrl = url.replace(/insureds.*/, `insureds/${insuredId}/records/${requirementId}`);

  const [appliedTemplate, setAppliedTemplate] = useState(null);
  const [sourceTemplateId, setSourceTemplateId] = useState(null);

  const handleGoBack = () => {
    if (currentTemplate.requirementTemplateBlocks.length) {
      dispatch(showModal(
        () => <SaveChangesModal changedInfo="insured requirements" />,
        {
          title: 'Exit without Saving Requirements?',
          buttons: {
            component: StackedButtonGroup,
            primaryButtonOptions: {
              onClick: async () => {
                await dispatch(hideModal());
                queryClient.removeQueries(keys.companyQuestionCategoryBase(companyId));
                history.push(insuredUrl);
              },
              dataAnalyticsId: 'exit-without-saving-requirements'
            }
          }
        }
      ));
    }
  };

  const handleTemplateSelect = async e => {
    dispatch(createToast({
      title: 'Updating Blocks...',
      status: 'info'
    }));

    const template = await portalApi.getRequirementTemplate(e.id);

    if (template.id) {
      await dispatch(insuredRequirementTemplateBuilder
        .bulkDeleteAndCreateRequirementTemplateBlocks(template.requirement_template_blocks));

      dispatch(createSuccessToast({
        title: 'Blocks Updated',
        duration: 6000
      }));
    }

    setSourceTemplateId(e.id);
  };

  const onUploadContract = useUploadContractFlow(companyIdNumber, async extractedTemplate => {
    setAppliedTemplate(extractedTemplate);

    await handleTemplateSelect(extractedTemplate);
  });

  const handleSaveRequirements = async () => {
    const onSuccess = async () => {
      dispatch(insuredRequirementTemplateBuilder.setUpdatingRequirements(false));
      await queryClient.refetchQueries(insuredKeys.insuredActiveRecords(insuredId));
      queryClient.refetchQueries(keys.companyAllPolicyTypes(companyId));
      queryClient.removeQueries(recordKeys.recordPolicyRequirements(requirementId));
      queryClient.removeQueries(recordKeys.recordPolicies(requirementId));
      queryClient.removeQueries(recordKeys.recordDocumentRequirements(requirementId));
      queryClient.removeQueries(recordKeys.recordQuestionRequirements(requirementId));
      queryClient.removeQueries(recordKeys.recordDocuments(requirementId));
      queryClient.removeQueries(recordKeys.recordLanguageRequirements(requirementId));
      queryClient.removeQueries(recordKeys.recordAttachments(requirementId));
      queryClient.removeQueries(keys.companyQuestionCategoryBase(companyId));

      history.push(insuredUrl);
    };

    const onFail = () => {
      dispatch(insuredRequirementTemplateBuilder.setUpdatingRequirements(false));
    };

    await dispatch(insuredRequirementTemplateBuilder.setUpdatingRequirements(true));
    await dispatch(updateInsuranceRequirements(
      insuredId,
      Number(requirementId),
      currentTemplate.id,
      onSuccess,
      onFail,
      { extractedTemplateIds: sourceTemplateId ? [sourceTemplateId] : [], sourceTemplateId }
    ));
  };

  return (
    <Flex alignItems="flex-start" justifyContent="space-between" mb={2}>
      <VStack>
        <Text textStyle="displayXxs600">Update Requirements</Text>
        <BackButton
          text="Insured Profile"
          url={insuredUrl}
          onClick={handleGoBack}
        />
      </VStack>
      <HStack>
        <Box width="400px">
          <RequirementTemplateDropdownMenu
            companyId={companyIdNumber}
            defaultTemplate={appliedTemplate}
            showResetButton={false}
            onChange={handleTemplateSelect}
            onUploadContract={onUploadContract}
          />
        </Box>
        <Button
          data-cy={CypressIds.updateRequirementsPageSaveButton}
          dusk="update-requirements-page-save-requirements-button"
          isDisabled={!currentTemplate?.requirementTemplateBlocks.length || updatingRequirements}
          variant="gradientSuccess"
          onClick={handleSaveRequirements}
        >
          Save Requirements
        </Button>
      </HStack>
    </Flex>
  );
};

export default UpdateRequirementsHeader;
